/**
 * Настроить кадр — visual crop editor for a MASTER 01 image.
 *
 * Behaviour (per approved Sprint 02A.1 spec):
 *   • Fixed crop frame in the actual reader aspect ratio (desktop 16/10, mobile 3/4).
 *   • Image is draggable inside the frame with mouse or touch.
 *   • Zoom slider (1x → 3x), never stretches — always cover.
 *   • «Сбросить» returns to sensible automatic centered crop.
 *   • «Готово» persists focal_x/focal_y + zoom.
 *   • No numeric coordinates exposed to the author.
 *   • Modal is fixed to the viewport, safe-area aware, blocks scroll bleed.
 */
import { useEffect, useMemo, useRef, useState } from "react";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Aspect ratios that match the actual HERO slots in HeroRender.
 * Desktop uses the wider portion of variant A/B image column (roughly 5/6-ish
 * because it takes half the screen with min-height 640). We use 4/5 to keep the
 * portrait feel that adapts well to 9:16 source photos.
 * Mobile uses 3/4 to match the portrait cinematic slot.
 */
const ASPECT = {
  desktop: 4 / 5,   // width / height  → tall crop
  mobile: 3 / 4,
};

export default function CropModal({ open, mode, url, focalX = 0.5, focalY = 0.5, zoom = 1, onSave, onClose }) {
  const [fx, setFx] = useState(focalX);
  const [fy, setFy] = useState(focalY);
  const [z, setZ] = useState(zoom || 1);
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (open) { setFx(focalX ?? 0.5); setFy(focalY ?? 0.5); setZ(zoom || 1); }
  }, [open, focalX, focalY, zoom]);

  // Lock body scroll while modal is open — prevents iOS jump-to-top bleed.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const aspect = ASPECT[mode] || ASPECT.desktop;
  const src = url && url.startsWith("http") ? url : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${url || ""}`;

  const beginDrag = (clientX, clientY) => {
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    dragRef.current = { startX: clientX, startY: clientY, startFx: fx, startFy: fy, w: rect.width, h: rect.height };
  };
  const moveDrag = (clientX, clientY) => {
    const d = dragRef.current;
    if (!d) return;
    // Convert pixel delta into focal-point delta.
    // With cover + object-position, moving image right ↔ focal shifts LEFT (lower fx).
    // The visible range for focal at zoom z is 1/z of full image, so sensitivity ~= 1 / (w * (z-1) + w).
    const dx = (clientX - d.startX) / d.w;
    const dy = (clientY - d.startY) / d.h;
    // At higher zoom, focal has bigger effective range → invert sensitivity by z.
    const nfx = clamp(d.startFx - dx / z, 0, 1);
    const nfy = clamp(d.startFy - dy / z, 0, 1);
    setFx(nfx); setFy(nfy);
  };
  const endDrag = () => { dragRef.current = null; };

  const onMouseDown = (e) => { e.preventDefault(); beginDrag(e.clientX, e.clientY); };
  const onMouseMove = (e) => { if (dragRef.current) moveDrag(e.clientX, e.clientY); };
  const onMouseUp = () => endDrag();
  const onTouchStart = (e) => { const t = e.touches[0]; beginDrag(t.clientX, t.clientY); };
  const onTouchMove = (e) => { e.preventDefault(); const t = e.touches[0]; moveDrag(t.clientX, t.clientY); };
  const onTouchEnd = () => endDrag();

  const reset = () => { setFx(0.5); setFy(0.5); setZ(1); };
  const done = () => { onSave({ focal_x: fx, focal_y: fy, zoom: z }); };

  // Style — image inside frame, cover, focal + scale.
  const imageStyle = {
    width: "100%", height: "100%",
    objectFit: "cover",
    objectPosition: `${fx * 100}% ${fy * 100}%`,
    transform: `scale(${z})`,
    transformOrigin: `${fx * 100}% ${fy * 100}%`,
    transition: dragRef.current ? "none" : "transform .12s ease-out",
    userSelect: "none",
    pointerEvents: "none",
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-3 py-6"
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
      data-testid="hero-crop-modal">
      <div className="w-full max-w-[520px] bg-cream rounded-2xl overflow-hidden shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-espresso/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">Настроить кадр</div>
            <div className="font-serif text-lg text-espresso">
              {mode === "mobile" ? "Мобильная композиция" : "Настольная композиция"}
            </div>
          </div>
          <button onClick={onClose}
            className="text-sm opacity-70 hover:opacity-100"
            data-testid="hero-crop-cancel">Отмена</button>
        </div>

        <div className="p-4">
          <div
            ref={frameRef}
            className="relative mx-auto overflow-hidden rounded-lg bg-neutral-100 touch-none select-none"
            style={{ width: "100%", aspectRatio: aspect }}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            data-testid="hero-crop-frame">
            {src && <img src={src} alt="" draggable={false} style={imageStyle} />}
            {/* subtle guide grid */}
            <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="border border-white/25" />
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-widest opacity-70">Приближение</span>
            <input type="range" min="1" max="3" step="0.05" value={z}
              onChange={(e) => setZ(parseFloat(e.target.value))}
              className="flex-1 accent-[#3B241A]"
              data-testid="hero-crop-zoom" />
            <span className="w-10 text-right text-[12px] opacity-70">{z.toFixed(1)}x</span>
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={reset}
              className="px-4 py-2 rounded-full border border-espresso/25 text-sm"
              data-testid="hero-crop-reset">Сбросить</button>
            <button
              onClick={done}
              className="px-6 py-2 rounded-full bg-espresso text-cream text-sm tracking-wide"
              data-testid="hero-crop-done">Готово</button>
          </div>

          <div className="mt-3 text-[11px] opacity-60 text-center">
            Двигайте фото пальцем или мышкой, чтобы разместить главный объект в центре.
          </div>
        </div>
      </div>
    </div>
  );
}
